import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Loading, ErrorBox, EmptyBox } from '../components/admin/ui';
import { Tooltip } from '../components/Tooltip';
import { TOOLTIPS, type TooltipKey } from '../lib/tooltips';
import { CardInccVigente } from '../components/CardInccVigente';

/** Tela /resultado (cliente, Fase 4 C1): KPIs + evolução do score + antes×depois. */

function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

const FLAG_LABEL: Record<string, string> = {
  parcela_atrasada: 'Parcela atrasada',
  reajuste_em_7d: 'Reajuste em 7 dias',
  reajuste_em_30d: 'Reajuste em 30 dias',
  padrao_piorando: 'Padrão piorando',
  indice_incc_faltante: 'INCC faltante',
  saldo_estimado: 'Saldo estimado',
};

function Kpi({ label, valor, sub, tip }: { label: string; valor: string; sub?: string; tip?: TooltipKey }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-1.5 text-xs text-ink-3">
        {label}
        {tip && <Tooltip text={TOOLTIPS[tip]} label={`O que é ${label}`} />}
      </div>
      <div className="num mt-1 text-2xl font-semibold text-ink">{valor}</div>
      {sub && <div className="mt-0.5 text-xs text-ink-4">{sub}</div>}
    </div>
  );
}

function Sparkline({ serie }: { serie: { semana: string; scoreMedio: number }[] }) {
  if (serie.length < 2) return <div className="text-sm text-ink-4">Série insuficiente ainda.</div>;
  const w = 560;
  const h = 120;
  const pad = 12;
  const vals = serie.map((s) => s.scoreMedio);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const xs = serie.map((_, i) => pad + (i * (w - 2 * pad)) / (serie.length - 1));
  const ys = vals.map((v) => h - pad - ((v - min) / (max - min || 1)) * (h - 2 * pad));
  const d = xs.map((x, i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${ys[i]!.toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" role="img" aria-label="Evolução do score médio">
      <path d={d} fill="none" stroke="var(--accent)" strokeWidth="2.5" />
      {xs.map((x, i) => (
        <circle key={i} cx={x} cy={ys[i]} r="3" fill="var(--accent)" />
      ))}
    </svg>
  );
}

export function ResultadoPage() {
  const { data, loading, error, reload } = useAsync(() => trpc.resultado.kpis.query());

  return (
    <div className="mx-auto max-w-4xl" data-testid="resultado-page">
      <h1 className="text-2xl font-semibold text-ink">Resultado</h1>
      <p className="mt-1 text-sm text-ink-3">Como sua carteira evolui com o monitoramento ativo.</p>

      <div className="mt-4">
        <CardInccVigente />
      </div>

      <div className="mt-4">
        {loading && <Loading />}
        {error && <ErrorBox msg={error} onRetry={reload} />}
        {data && data.contratosMonitorados === 0 && (
          <EmptyBox titulo="Sem dados ainda" descricao="Importe contratos e rode a varredura para ver o resultado." />
        )}
        {data && data.contratosMonitorados > 0 && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi label="Contratos monitorados" valor={String(data.contratosMonitorados)} />
              <Kpi label="Carteira em risco" valor={brl(data.carteiraEmRisco)} sub="score ≥ 40" tip="kpiCarteiraRisco" />
              <Kpi label="E-mails enviados" valor={String(data.comunicacoes.enviadas + data.comunicacoes.simuladas)} sub={`${data.comunicacoes.aguardando} aguardando`} />
              <Kpi
                label="Taxa de atraso"
                valor={data.atrasoDepois != null ? `${(data.atrasoDepois * 100).toFixed(0)}%` : '-'}
                sub={data.atrasoAntes != null ? `entrada: ${(data.atrasoAntes * 100).toFixed(0)}%` : undefined}
                tip="resultadoAntesAgora"
              />
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
              <div className="card p-5">
                <div className="flex items-center gap-1.5 text-[15px] font-semibold text-ink">
                  Evolução do score médio
                  <Tooltip text={TOOLTIPS.resultadoTendencia} label="O que é a evolução do score médio" />
                </div>
                <div className="mt-0.5 text-xs text-ink-3">últimas semanas · menor é melhor</div>
                <div className="mt-3">
                  <Sparkline serie={data.serie} />
                </div>
              </div>
              <div className="card p-5">
                <div className="text-[15px] font-semibold text-ink">Flags de risco por tipo</div>
                <div className="mt-3 space-y-2">
                  {data.flagsPorTipo.length === 0 && <div className="text-sm text-ink-4">Nenhuma flag.</div>}
                  {data.flagsPorTipo.map((f) => (
                    <div key={f.tipo} className="flex items-center justify-between text-sm">
                      <span className="text-ink-2">{FLAG_LABEL[f.tipo] ?? f.tipo}</span>
                      <span className="num font-medium text-ink">{f.n}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
